<script lang="ts" setup>
import Icon from '@/components/shared/Icon.vue'
import { generateIconColor } from '@/lib'
import { Switch, SwitchGroup, SwitchLabel } from '@headlessui/vue'
import { useId } from 'radix-vue'
import { computed } from 'vue'
import { useToggleField } from './useToggleField'
import FieldHelpText from '@/components/shared/Fields/FieldHelpText.vue'
import FieldErrorMessage from '@/components/shared/Fields/FieldErrorMessage.vue'

interface ToggleProps {
  trueValue?: any
  defaultChecked?: boolean
  showLabel?: boolean
  disabled?: boolean
  validate?: boolean
  color?: string
  prefixIcon?: string
  suffixIcon?: string
  variant?: 'default' | 'ghost'
  size?: 'sm' | 'md'
  help?: string
  label?: string
  name: string
}

const props = withDefaults(defineProps<ToggleProps>(), {
  defaultChecked: undefined,
  validate: true,
  showLabel: true,
  disabled: false,
  variant: 'default',
  size: 'md',
})

const { value, errorMessage, booleanValue, handleUpdate } = useToggleField({
  name: props.name,
  defaultChecked: props.defaultChecked,
  trueValue: props.trueValue,
  validate: props.validate,
})

const sizeSwitchClasses = computed(() => ({
  'size-md': props.size === 'md',
  'size-sm': props.size === 'sm',
}))

const switchBaseClasses =
  'inline-flex relative flex-shrink-0 rounded-full border-2 border-transparent transition-colors ease-in-out cursor-pointer duration-50 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900'

const switchDimensionClasses = computed(() => ({
  'w-11 h-6': props.size === 'md',
  'w-9 h-5': props.size === 'sm',
}))

const switchStateClasses = computed(() => ({
  '!cursor-not-allowed opacity-60': props.disabled,
  'bg-gray-200/80 dark:bg-gray-700/80': props.disabled && !value.value,
  'bg-primary-300 dark:bg-primary-700/70': props.disabled && value.value,
  'bg-error-50 ring-1 ring-error-300 dark:bg-error-800/50 dark:ring-error-500/50':
    errorMessage.value,
  'bg-primary-500 dark:bg-primary-600': value.value && props.variant === 'default',
  'bg-gray-200 dark:bg-gray-800 dark:ring-gray-700':
    !value.value && !errorMessage.value && props.variant === 'default',
  'bg-white/20 border-white dark:bg-gray-800 dark:border-gray-700': props.variant === 'ghost',
  'focus:ring-primary-300': true,
}))

const toggleBaseClasses =
  'inline-block bg-white rounded-full ring-0 shadow transition duration-200 ease-out transform pointer-events-none dark:bg-gray-100 group-hover:scale-90 group-hover:duration-300'

const toggleDimensionClasses = computed(() => ({
  'w-5 h-5': props.size === 'md',
  'w-4 h-4': props.size === 'sm',
}))

const toggleStateClasses = computed(() => ({
  'opacity-90 backdrop-blur-sm bg-gray-100 dark:bg-gray-400': props.disabled,
  'translate-x-5': value.value && props.size === 'md',
  'translate-x-4': value.value && props.size === 'sm',
  'ring-1 ring-inset ring-error-300 dark:bg-error-400/40 dark:ring-error-500/50':
    errorMessage.value,
  'bg-white dark:bg-gray-100': props.variant === 'ghost',
}))

const labelClasses = computed(() => ({
  'ml-3': true,
  'text-xs': props.size === 'sm',
  'text-sm': props.size === 'md',
  'text-white dark:text-gray-100': props.variant === 'ghost',
  'text-gray-900 dark:text-gray-300': props.variant === 'default',
  '!cursor-not-allowed text-gray-400 dark:text-gray-600': props.disabled,
}))

const id = useId()
</script>

<template>
  <div class="inline-flex gap-x-3 items-center">
    <Icon
      v-if="prefixIcon"
      :variant="prefixIcon"
      :class="['shrink-0', props.size === 'sm' ? 'size-4' : 'size-5']"
      v-bind="generateIconColor(errorMessage ? 'error' : color)"
    />
    <SwitchGroup
      :id="id"
      as="div"
      class="flex items-center"
      :class="{ 'cursor-not-allowed': props.disabled }"
    >
      <Switch
        :model-value="booleanValue"
        :disabled="disabled"
        class="group"
        :class="[switchBaseClasses, switchDimensionClasses, switchStateClasses]"
        @update:model-value="handleUpdate"
        @click.stop
      >
        <span
          aria-hidden="true"
          :class="[toggleBaseClasses, toggleDimensionClasses, toggleStateClasses]"
        />
      </Switch>
      <SwitchLabel v-if="showLabel" as="span" :class="labelClasses">
        <span v-if="label" class="font-medium">{{ label }}</span>
        <sup
          v-if="validate"
          class="ml-0.5"
          :class="{
            'text-error-500 dark:text-error-400': variant === 'default',
            'text-white': variant === 'ghost',
          }"
          >*</sup
        >
      </SwitchLabel>
    </SwitchGroup>

    <Icon
      v-if="suffixIcon"
      :variant="suffixIcon"
      :class="['shrink-0', props.size === 'sm' ? 'size-4' : 'size-5']"
      v-bind="generateIconColor(errorMessage ? 'error' : color)"
    />
  </div>

  <div
    v-if="help || errorMessage"
    :class="{
      'pl-8': prefixIcon && props.size === 'md',
      'pl-7': prefixIcon && props.size === 'sm',
    }"
  >
    <FieldHelpText v-if="help" :name="name" :help-text="help" />
    <FieldErrorMessage v-if="errorMessage" :name="name" :error-message="errorMessage" />
  </div>
</template>
